import { supabase } from "../lib/supabase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface WarehouseMonitoringStats {
  latestCount: number;
  eventsCount: number;
  eventsLast24h: number;
  hotelsWithData: number;
  hotelBreakdown: Array<{ hotelCode: string; count: number }>;
  pmsBreakdown: Array<{ pms: string; count: number }>;
  eventTypeBreakdown: Array<{ eventType: string; count: number }>;
}

export interface WarehouseMonitoringEvent {
  id: number;
  hotelCode: string;
  pms: string;
  reservationId: string;
  eventType: string;
  sourceEndpoint: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  ingestedAt: string;
  requestId: string | null;
}

export interface WarehouseMonitoringLatestRow {
  hotelCode: string;
  pms: string;
  reservationId: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  lastSeenAt: string;
  lastSourceEndpoint: string;
  ingestedAt: string;
}

export interface PaginatedMeta {
  page: number;
  pageSize: number;
  total: number;
}

export interface WarehouseEventsFilters {
  hotelCode?: string;
  pms?: string;
  eventType?: string;
  reservationId?: string;
  page?: number;
  pageSize?: number;
}

export interface WarehouseLatestFilters {
  hotelCode?: string;
  pms?: string;
  reservationId?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

class WarehouseMonitoringService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      throw new Error("Failed to get session information");
    }

    if (!session) {
      throw new Error("You must be logged in to access warehouse monitoring");
    }

    return {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    };
  }

  private buildQuery(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        search.set(key, String(value));
      }
    });
    const query = search.toString();
    return query ? `?${query}` : "";
  }

  private async request<T>(path: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      let errorMessage = `Request failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch {
          // keep default
        }
      }

      if (response.status === 403) {
        errorMessage = `Permission denied: ${errorMessage}`;
      } else if (response.status === 401) {
        errorMessage = `Authentication failed: ${errorMessage}`;
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getStats(): Promise<WarehouseMonitoringStats> {
    const response = await this.request<{ success: boolean; stats: WarehouseMonitoringStats }>(
      "/api/internal/warehouse/stats"
    );
    return response.stats;
  }

  async getEvents(
    filters: WarehouseEventsFilters = {}
  ): Promise<{ data: WarehouseMonitoringEvent[]; meta: PaginatedMeta }> {
    const query = this.buildQuery(filters);
    return this.request(`/api/internal/warehouse/events${query}`);
  }

  async getLatest(
    filters: WarehouseLatestFilters = {}
  ): Promise<{ data: WarehouseMonitoringLatestRow[]; meta: PaginatedMeta }> {
    const query = this.buildQuery(filters);
    return this.request(`/api/internal/warehouse/latest${query}`);
  }

  async getPayload(
    hotelCode: string,
    reservationId: string,
    pms?: string,
    source: "latest" | "events" = "latest"
  ): Promise<unknown | null> {
    const query = this.buildQuery({ pms, source });
    const response = await this.request<{ success: boolean; data: unknown | null }>(
      `/api/internal/warehouse/reservations/${encodeURIComponent(hotelCode)}/${encodeURIComponent(reservationId)}/payload${query}`
    );
    return response.data;
  }

  async getAnalytics(
    filters: WarehouseAnalyticsFilters = {}
  ): Promise<WarehouseAnalyticsResult> {
    const query = this.buildQuery(filters);
    const response = await this.request<{ success: boolean; analytics: WarehouseAnalyticsResult }>(
      `/api/internal/warehouse/analytics${query}`
    );
    return response.analytics;
  }

  async getCrmProfiles(
    filters: WarehouseCrmFilters = {}
  ): Promise<WarehouseCrmProfilesResult> {
    const query = this.buildQuery(filters);
    return this.request<WarehouseCrmProfilesResult>(`/api/internal/warehouse/crm/profiles${query}`);
  }

  async getCrmProfileReservations(
    email: string,
    filters: Omit<WarehouseCrmFilters, "email"> = {}
  ): Promise<WarehouseCrmProfileReservationsResult> {
    const query = this.buildQuery(filters);
    return this.request<WarehouseCrmProfileReservationsResult>(
      `/api/internal/warehouse/crm/profiles/${encodeURIComponent(email)}/reservations${query}`
    );
  }
}

export type WarehouseAnalyticsGroupBy = "day" | "month";

export interface WarehouseAnalyticsFilters {
  hotelCode?: string;
  pms?: string;
  from?: string;
  to?: string;
  groupBy?: WarehouseAnalyticsGroupBy;
}

export interface WarehouseAnalyticsPeriodRow {
  period: string;
  reservations: number;
  revenue: number;
  cancelled: number;
  nights: number;
}

export interface WarehouseAnalyticsBreakdownRow {
  key: string;
  count: number;
  revenue: number;
}

export interface WarehouseAnalyticsResult {
  summary: {
    totalReservations: number;
    activeReservations: number;
    cancelledReservations: number;
    totalRevenue: number;
    averageRevenue: number;
    averageNights: number;
    totalNights: number;
    cancellationRate: number;
    currencyCode: string | null;
  };
  byPeriod: WarehouseAnalyticsPeriodRow[];
  byStatus: WarehouseAnalyticsBreakdownRow[];
  byPms: WarehouseAnalyticsBreakdownRow[];
  byChannel: WarehouseAnalyticsBreakdownRow[];
  byHotel: WarehouseAnalyticsBreakdownRow[];
  filters: {
    from: string;
    to: string;
    groupBy: WarehouseAnalyticsGroupBy;
    hotelCode?: string;
    pms?: string;
    rowsAnalyzed: number;
    truncated: boolean;
  };
}

export const warehouseMonitoringService = new WarehouseMonitoringService();

export interface WarehouseCrmFilters {
  hotelCode?: string;
  pms?: string;
  email?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface WarehouseCrmProfile {
  email: string;
  displayName: string | null;
  reservationCount: number;
  activeReservations: number;
  cancelledReservations: number;
  totalRevenue: number;
  totalNights: number;
  hotelCodes: string[];
  pmsList: string[];
  firstStayAt: string | null;
  lastStayAt: string | null;
  currencyCode: string | null;
}

export interface WarehouseCrmProfileReservation {
  hotelCode: string;
  pms: string;
  reservationId: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  revenue: number;
  nights: number;
  guestName: string | null;
  channel: string | null;
  lastSeenAt: string | null;
}

export interface WarehouseCrmProfilesResult {
  success: boolean;
  summary: {
    totalProfiles: number;
    totalReservationsWithEmail: number;
    reservationsWithoutEmail: number;
    rowsAnalyzed: number;
    truncated: boolean;
  };
  data: WarehouseCrmProfile[];
  meta: PaginatedMeta;
  filters: {
    hotelCode?: string;
    pms?: string;
    email?: string;
    from?: string;
    to?: string;
  };
}

export interface WarehouseCrmProfileReservationsResult {
  success: boolean;
  profile: WarehouseCrmProfile | null;
  data: WarehouseCrmProfileReservation[];
  meta: PaginatedMeta;
}
